import { applyTransformations } from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { PlayerContextState, PlayerContextTransform } from "../PlayerContext";
import { applyAllTransforms } from "../PlayerContextListeners";
import { OfflineTickListeners } from "./OfflineTickListeners";
import { WarpTimeListeners } from "./WarpTimeListeners";

type TimeTickListener = (
  state: PlayerContextState,
  timeElapsedSec: number,
  isWarp: boolean,
) => PlayerContextState;

const listeners: Record<string, TimeTickListener> = {};

export function nextFrame() {
  return new Promise(function (resolve, reject) {
    requestAnimationFrame(function () {
      resolve(null);
    });
  });
}

export function registerTimeTickListener(
  id: string,
  listener: TimeTickListener,
) {
  listeners[id] = listener;
}

export function processTimeTick(
  this: any,
  timeElapsedSec: number,
  isWarp: boolean,
): PlayerContextTransform {
  return (state) => {
    const speedMultiplier = applyTransformations(
      [TransformationTags.GameSpeed],
      state,
      1.0,
      { isWarp: isWarp },
    );
    const timeElapsedSecAfterTransform = timeElapsedSec * speedMultiplier;
    Object.values(listeners).forEach((listener) => {
      state = listener(state, timeElapsedSecAfterTransform, isWarp);
    });
    return state;
  };
}

const WARP_TIME_PROCESSING_CONFIG_DEFAULT = {
  secsPerTick: 0.5,
};

let warpTimeProcessingConfig = WARP_TIME_PROCESSING_CONFIG_DEFAULT;

export function setWarpTimeSecsPerTick(secsPerTick: number): void {
  warpTimeProcessingConfig = { secsPerTick: secsPerTick };
}

export async function processOfflineTicks(
  initialState: PlayerContextState,
  secsTotal: number,
  lastProcessedTime: number,
  setPlayerState: (state: PlayerContextState) => void,
): Promise<void> {
  let currentState = initialState;
  currentState.lastProcessedTime = lastProcessedTime;

  OfflineTickListeners.getAll().forEach((listener) => {
    const transform = listener.getListener()(secsTotal);
    if (transform) {
      currentState = transform(currentState);
    }
  });

  setPlayerState(currentState);
}

export async function processWarpTime(
  initialState: PlayerContextState,
  lastProcessedTime: () => number,
  onLoadingProgress: (secsProcessedSoFar: number, ticksTotal: number) => void,
  setPlayerState: (state: PlayerContextState) => void,
): Promise<void> {
  warpTimeProcessingConfig = WARP_TIME_PROCESSING_CONFIG_DEFAULT;
  const secsTotal = initialState.warpTimeSecs;
  let secsProcessedSoFar = 0;
  let currentState = initialState;
  onLoadingProgress(secsProcessedSoFar, secsTotal);
  let ticksProcessedSoFar = 0;
  while (secsProcessedSoFar < secsTotal) {
    const speedMultiplier = applyTransformations(
      [TransformationTags.GameSpeed],
      currentState,
      1.0,
      { isWarp: true },
    );
    const secsPerTick =
      warpTimeProcessingConfig.secsPerTick / Math.min(8, speedMultiplier);
    const secsToProcess = Math.min(secsPerTick, secsTotal - secsProcessedSoFar);
    currentState = applyAllTransforms(
      currentState,
      processTimeTick(secsToProcess, true),
    );
    secsProcessedSoFar += secsToProcess;
    ticksProcessedSoFar += 1;
    if ((ticksProcessedSoFar - 1) % 100 == 0) {
      onLoadingProgress(secsProcessedSoFar, secsTotal);
      await nextFrame();
    }
  }
  currentState.lastProcessedTime = lastProcessedTime();
  currentState.warpTimeSecs = 0;

  WarpTimeListeners.getAll().forEach((listener) => {
    const transform = listener.getListener()(secsTotal);
    if (transform) {
      currentState = transform(currentState);
    }
  });

  setPlayerState(currentState);
  onLoadingProgress(secsTotal, secsTotal);
}
